import { useCallback, useRef } from 'react';
import { useNativeIntersectionObserver } from './useNativeIntersectionObserver.js';

type Callback<T extends HTMLElement> = (entry: Omit<IntersectionObserverEntry, 'target'> & { target: T }) => void;

// Uses native intersection observer to trigger the callback when the
// element is intersecting the viewport and the position of the element
// is changed
export const useTrackElement = <T extends HTMLElement>(el: T | null, callback: Callback<T>) => {
  const prevPosition = useRef<{ x: number | null; y: number | null }>({ x: null, y: null });

  const debouncedCallback: Callback<T> = useCallback(
    (entry) => {
      if (
        entry.boundingClientRect.x !== prevPosition.current.x ||
        entry.boundingClientRect.y !== prevPosition.current.y
      ) {
        callback(entry);
      }
      prevPosition.current = { x: entry.boundingClientRect.x, y: entry.boundingClientRect.y };
    },
    [callback],
  );

  useNativeIntersectionObserver(el, debouncedCallback);
};
